import { CartService, CartServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { Impersonation, UserSession } from '@b2b/common/services/auth/types';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ACMEResponse, TODO, UIBModalService } from '@b2b/types/core';
import { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IScope, ITimeoutService } from 'angular';

SherpaCustomersTenantsController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  '$uibModal',
  'ApiService',
  'HelpersService',
  'PermissionsService',
  'CartService',
  'FeatureToggleService',
  'AuthenticationService',
];

function SherpaCustomersTenantsController(
  $scope: IScope & {
    sherpaCustomersTenants: TODO;
    gridOptions: TODO;
    gridNameLink: string;
    deputySignIn: string;
    ordersLink: string;
    activityLink: string;
  },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $uibModal: UIBModalService,
  ApiService: ApiService,
  HelpersService: TODO,
  PermissionsService: PermissionsService,
  CartService: CartService,
  FeatureToggleService: TODO,
  authenticationService: AuthenticationService
) {
  $scope.gridNameLink =
    '<div class="ui-grid-cell-contents"><a data-ng-click="grid.appScope.getB2C(row)" role="link">{{row.entity.name}}</a></div>';
  $scope.deputySignIn =
    '<div class="ui-grid-cell-contents"><a data-ng-click="grid.appScope.deputySignIn($event, row, row.entity.id)" role="button">CSR Sign In</a></div>';
  $scope.ordersLink =
    '<div class="ui-grid-cell-contents"><a data-ng-click="grid.appScope.getCustomerOrders(row)" role="link">Orders</a></div>';
  $scope.activityLink =
    '<div class="ui-grid-cell-contents"><a data-ng-click="grid.appScope.getTenantActivity(row)" role="link">Activity</a></div>';

  const ctrl = {
    tenants: [] as TODO[],
    searchQuery: '',
    isLoaded: false,
    filtered: [] as TODO[],

    init: function () {
      $scope.$emit('Application::contentTitleChange', { title: 'Tenants' });
      ctrl.getTenants();
    },

    getTenants: function () {
      ApiService.customerService
        .listTenants()
        .then((response) => ctrl.success.getTenants(response))
        .catch((response) => ctrl.error.getTenants(response));
    },

    completeSignIn: function (id) {
      ApiService.user.permissions
        .list(id)
        .then((response) => ctrl.success.completeSignIn(response))
        .catch((response) => ctrl.error.completeSignIn(response));
    },

    deputySignIn: function (e, row, id) {
      $uibModal
        .open({
          templateUrl: 'views/application/views/sherpa/customers/modals/signIn.tpl.html',
          controller: 'SherpaCustomersSignInModalController',
          resolve: {
            currentTenant: () => {
              return {
                id: id,
                name: row.entity.name,
              };
            },
          },
        })
        .result.then(() => {
          authenticationService
            .deputySignIn(id)
            .then((response) => ctrl.success.deputySignIn(response))
            .catch((response) => ctrl.error.deputySignIn(response));
        })
        .catch(() => {
          // noop
        });
    },

    getB2C: function (row) {
      window.location.href = row.entity.consumerPage + '/list';
    },

    getCustomerOrders: function (item) {
      $state.go('application.sherpa.customers.orders', { id: item.entity.id, type: 'tenant' });
    },

    getTenantActivity: function (item) {
      $state.go('application.sherpa.activities.tenant', { tenantId: item.entity.id });
    },

    search: function () {
      ctrl.filtered = $filter('filter')(ctrl.tenants, (tenant) => {
        const id = '' + tenant.id || '';
        const name = tenant.name || '';
        const value = (id + name).toLowerCase();

        return value.indexOf(ctrl.searchQuery.toLowerCase()) !== -1 ? tenant : null;
      });
    },

    success: {
      getTenants: function (response) {
        ctrl.tenants = response.data.payload;
        ctrl.search();
        ctrl.isLoaded = true;
      },
      deputySignIn: function (response: {
        data: ACMEResponse<{
          user: UserSession;
          impersonation: Impersonation | null;
        }>;
      }) {
        ctrl.completeSignIn(response.data.payload.user.id);
      },
      completeSignIn: function (response) {
        const onFeatureLoad = () => {
          $timeout(() => {
            CartService.resetCart();
            $state.go('home.default', {}, { reload: true });
            PermissionsService.refresh($scope);
          }, 10);
        };
        const permissions = response.data.payload;
        ApiService.tenant.config.list().then((response) => {
          if (response.data.payload.list.length > 0 && response.data.payload.list[0].recurringPaymentEnabled) {
            permissions.push('RECURRING_PAYMENT_ENABLED');
          }
          PermissionsService.clearChecksum();
          PermissionsService.store(permissions);
          FeatureToggleService.reload();
          FeatureToggleService.loaded().then(onFeatureLoad, onFeatureLoad);
        });
      },
    },
    error: {
      getTenants: function (response) {},
      deputySignIn: function (response) {},
      completeSignIn: function (response) {},
    },
  };

  $scope.gridOptions = {
    enableColumnResizing: false,
    multiselect: false,
    enableRowSelection: false,
    paginate: true,
    showFooter: true,
    pageSize: 50,
    rowHeight: 24,

    appScopeProvider: {
      deputySignIn: ctrl.deputySignIn,
      getB2C: ctrl.getB2C,
      getCustomerOrders: ctrl.getCustomerOrders,
      getTenantActivity: ctrl.getTenantActivity,
    },

    columnDefs: [
      {
        field: 'id',
        displayName: 'ID',
        visible: true,
      },
      {
        field: 'consumerPage',
        displayName: 'Consumer Page',
        visible: false,
      },
      {
        field: 'name',
        displayName: 'Name',
        onCellNavClick: (row, col) => {
          $scope.gridOptions.appScopeProvider.getB2C(row);
        },
        cellTemplate: $scope.gridNameLink,
        sort: {
          direction: 'asc',
        },
      },
      {
        field: 'deputy',
        displayName: 'Guide',
        onCellNavClick: (row, col) => {
          $scope.gridOptions.appScopeProvider.deputySignIn(null, row, row.entity.id);
        },
        cellTemplate: $scope.deputySignIn,
      },

      {
        field: 'orders',
        displayName: 'Orders',
        onCellNavClick: (row, col) => {
          $scope.gridOptions.appScopeProvider.getCustomerOrders(row);
        },
        cellTemplate: $scope.ordersLink,
      },
      {
        field: 'activity',
        displayName: 'Activity',
        onCellNavClick: (row, col) => {
          $scope.gridOptions.appScopeProvider.getTenantActivity(row);
        },
        cellTemplate: $scope.activityLink,
      },
    ],
  };

  $scope.sherpaCustomersTenants = ctrl;
  ctrl.init();
}

export const SherpaCustomersTenantsControllerModule = angular
  .module('acme.view.application.sherpa.customers.tenants.controllers', [
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.permissions',
    CartServiceModule,
    'acme.common.service.featureToggle',
    AuthenticationServiceModule,
    'acme.view.application.sherpa.customers.signInModal.controllers',
  ])
  .controller('SherpaCustomersTenantsController', SherpaCustomersTenantsController).name;
